import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Compass, Layers, Users, Loader2, Home, Menu, Calendar, LogIn, Shield, LogOut, Navigation } from 'lucide-react';
import ExhibitionScene3D from '@/components/ExhibitionScene3D';
import BoothInfoPanel from '@/components/BoothInfoPanel';
import SearchBar from '@/components/SearchBar';
import FilterPanel from '@/components/FilterPanel';
import WeatherWidget from '@/components/WeatherWidget';
import VisitorPanel from '@/components/VisitorPanel';
import AnnouncementBanner from '@/components/AnnouncementBanner';
import { authApi } from '@/lib/api';
import { useVisitorGPS } from '@/hooks/useVisitorGPS';
import GPSLocateButton from '@/components/GPSLocateButton';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ExhibitionPage() {
  const [booths, setBooths] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [sceneProps, setSceneProps] = useState([]);
  const [landmarks, setLandmarks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [halls, setHalls] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedBooth, setSelectedBooth] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedHall, setSelectedHall] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showWelcome, setShowWelcome] = useState(true);
  const [weather, setWeather] = useState(null);
  const gps = useVisitorGPS();
  const [effectsEnabled, setEffectsEnabled] = useState(false); // OFF by default for clear view
  const [showFacilities, setShowFacilities] = useState(true);
  const [routeEnd, setRouteEnd] = useState(null); // [x, 0, z]
  const [routeEndBooth, setRouteEndBooth] = useState(null);
  const routeStart = [-75, 0, 25]; // Entrance archway position (left side)

  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      try {
        const [boothsRes, facilitiesRes, categoriesRes, hallsRes, statsRes, propsRes, landmarksRes] = await Promise.all([
          axios.get(`${API_URL}/booths`),
          axios.get(`${API_URL}/facilities`),
          axios.get(`${API_URL}/categories`),
          axios.get(`${API_URL}/halls`),
          axios.get(`${API_URL}/stats`),
          axios.get(`${API_URL}/props`),
          axios.get(`${API_URL}/landmarks`),
        ]);

        setBooths(boothsRes.data.data || []);
        setFacilities(facilitiesRes.data.data || []);
        setCategories(categoriesRes.data.data || []);
        setHalls(hallsRes.data.data || []);
        setStats(statsRes.data.data);
        setSceneProps(propsRes.data.data || []);
        setLandmarks(landmarksRes.data.data || []);
      } catch (error) {
        console.error('Error fetching exhibition data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchInitialData();
  }, []);

  // Handler for "Take me there" from booth info panel
  const handleNavigateToBooth = (booth) => {
    setRouteEnd([booth.position_x, 0, booth.position_z]);
    setRouteEndBooth(booth);
    setSelectedBooth(null); // close info panel to see the route
  };

  const clearRoute = () => {
    setRouteEnd(null);
    setRouteEndBooth(null);
  };

  // Filter booths locally based on selected filters
  const filteredBooths = useMemo(() => {
    let filtered = [...booths];
    
    if (selectedCategory) {
      filtered = filtered.filter(b => b.category_id === selectedCategory);
    }
    
    if (selectedHall) {
      filtered = filtered.filter(b => b.hall_id === selectedHall);
    }
    
    return filtered;
  }, [booths, selectedCategory, selectedHall]);

  // Use raw seeder positions directly (they now match the official diagram layout)
  const positionedBooths = filteredBooths;

  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-blue-400 animate-spin mx-auto mb-4" />
          <div className="text-2xl font-bold text-white mb-2">Loading Exhibition</div>
          <div className="text-slate-400">Initializing 3D outdoor space...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-emerald-950 via-slate-900 to-emerald-950 overflow-hidden">
      {/* Top Navigation Bar */}
      <div className="absolute top-0 left-0 right-0 z-30 p-4">
        <div className="flex items-center gap-4">
          {/* Logo */}
          <div className="flex items-center gap-3 bg-gradient-to-r from-emerald-900/90 via-emerald-800/90 to-emerald-900/90 backdrop-blur-xl rounded-2xl px-4 py-2.5 border border-emerald-500/30 shadow-xl" data-testid="app-logo">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-400 via-green-500 to-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-500/30">
              <span className="text-2xl">🌿</span>
            </div>
            <div className="hidden sm:block">
              <div className="text-white font-black text-xs leading-none tracking-wider">HARI LANDSKAP</div>
              <div className="text-emerald-300 text-[10px] font-bold">NEGARA 2026</div>
              <div className="text-emerald-400/70 text-[9px] mt-0.5">📍 Stadium Hang Jebat, Melaka</div>
            </div>
          </div>

          {/* Search bar */}
          <div className="flex-1 max-w-2xl">
            <SearchBar onBoothSelect={setSelectedBooth} />
          </div>

          {/* Quick nav */}
          <Link to="/schedule" className="hidden md:flex items-center gap-1.5 px-3 py-3 rounded-2xl bg-slate-900/70 backdrop-blur-xl border border-slate-700/50 text-white hover:bg-slate-800/70 transition-colors shadow-xl text-xs font-bold" data-testid="nav-schedule-btn">
            <Calendar className="w-4 h-4 text-emerald-400" /><span className="hidden lg:inline">Schedule</span>
          </Link>
          {authApi.isAuthenticated() ? (
            <>
              {authApi.isAdmin() && (
                <Link to="/admin" className="hidden md:flex items-center gap-1.5 px-3 py-3 rounded-2xl bg-emerald-500/20 backdrop-blur-xl border border-emerald-500/30 text-emerald-200 hover:bg-emerald-500/30 transition-colors shadow-xl text-xs font-bold" data-testid="nav-admin-btn">
                  <Shield className="w-4 h-4" /><span className="hidden lg:inline">Admin</span>
                </Link>
              )}
              <button onClick={() => { authApi.logout(); window.location.reload(); }} className="hidden md:flex items-center gap-1.5 px-3 py-3 rounded-2xl bg-red-500/20 backdrop-blur-xl border border-red-500/30 text-red-200 hover:bg-red-500/30 transition-colors shadow-xl text-xs font-bold" data-testid="nav-logout-btn">
                <LogOut className="w-4 h-4" />
              </button>
            </>
          ) : (
            <Link to="/login" className="hidden md:flex items-center gap-1.5 px-3 py-3 rounded-2xl bg-slate-900/70 backdrop-blur-xl border border-slate-700/50 text-white hover:bg-slate-800/70 transition-colors shadow-xl text-xs font-bold" data-testid="nav-login-btn">
              <LogIn className="w-4 h-4 text-emerald-400" /><span className="hidden lg:inline">Login</span>
            </Link>
          )}

          {/* Toggle sidebar */}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-3 rounded-2xl bg-slate-900/70 backdrop-blur-xl border border-slate-700/50 text-white hover:bg-slate-800/70 transition-colors shadow-xl"
            data-testid="toggle-sidebar-btn"
          >
            <Menu className="w-5 h-5" />
          </button>
        </div>

        {/* Second row: Facility toggle + Theme badges */}
        <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
          <button
            onClick={() => setShowFacilities(!showFacilities)}
            className={`px-3 py-1.5 rounded-full text-[10px] font-black tracking-wider shadow-lg transition-all flex items-center gap-1.5 ${
              showFacilities 
                ? 'bg-blue-500 text-white' 
                : 'bg-slate-800/70 text-slate-300 border border-slate-700'
            }`}
            data-testid="toggle-facilities-btn"
          >
            <span>🏛️</span>
            {showFacilities ? 'HIDE FACILITIES' : 'SHOW FACILITIES'} ({facilities.length})
          </button>
          
          <div className="hidden md:flex items-center gap-1.5">
            {[
              {name: 'HIJAU', color: 'bg-green-500', text: 'text-green-100'},
              {name: 'INOVASI', color: 'bg-blue-500', text: 'text-blue-100'},
              {name: 'KOMUNITI', color: 'bg-pink-500', text: 'text-pink-100'},
              {name: 'LESTARI', color: 'bg-orange-500', text: 'text-orange-100'},
            ].map((theme) => (
              <div key={theme.name} className={`px-3 py-1 rounded-full ${theme.color} ${theme.text} text-[10px] font-black tracking-wider shadow-lg`}>
                {theme.name}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Weather Widget - Top left area (below logo) */}
      <WeatherWidget 
        onWeatherChange={setWeather} 
        effectsEnabled={effectsEnabled}
        onToggleEffects={() => setEffectsEnabled(v => !v)}
      />

      {/* Visitor floating panel (guest mode + bookmarks + visited + notes) */}
      <VisitorPanel booths={booths} />

      {/* Live announcements / flash sale banner + browser push */}
      <AnnouncementBanner />

      {/* Left Sidebar - Filters (positioned below collapsed weather widget; expanded weather overlaps intentionally on user demand) */}
      {sidebarOpen && (
        <div className="absolute left-4 top-56 bottom-24 w-72 z-20 animate-in slide-in-from-left duration-300 overflow-y-auto">
          <FilterPanel
            categories={categories}
            halls={halls}
            selectedCategory={selectedCategory}
            selectedHall={selectedHall}
            onCategoryChange={setSelectedCategory}
            onHallChange={setSelectedHall}
            boothStats={stats}
          />
        </div>
      )}

      {/* Bottom Info Bar */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20">
        <div className="bg-slate-900/70 backdrop-blur-xl rounded-2xl px-6 py-3 border border-slate-700/50 shadow-xl flex items-center gap-6">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-white text-sm font-medium">Live</span>
          </div>
          <div className="w-px h-6 bg-slate-700" />
          <div className="flex items-center gap-2 text-white text-sm">
            <MapPin className="w-4 h-4 text-blue-400" />
            <span>{filteredBooths.length} booths</span>
          </div>
          <div className="w-px h-6 bg-slate-700" />
          <div className="flex items-center gap-2 text-white text-sm">
            <Layers className="w-4 h-4 text-purple-400" />
            <span>{halls.length} zones</span>
          </div>
          <div className="w-px h-6 bg-slate-700" />
          <div className="flex items-center gap-2 text-white text-sm">
            <Users className="w-4 h-4 text-emerald-400" />
            <span>{stats?.total_companies || 0} exhibitors</span>
          </div>
        </div>
      </div>

      {/* Compass Indicator */}
      <div className="absolute bottom-4 right-4 z-20">
        <div className="bg-slate-900/70 backdrop-blur-xl rounded-2xl p-3 border border-slate-700/50 shadow-xl" data-testid="compass">
          <div className="relative w-12 h-12">
            <div className="absolute inset-0 rounded-full border-2 border-slate-700" />
            <div className="absolute top-1 left-1/2 -translate-x-1/2 text-red-400 text-xs font-bold">N</div>
            <div className="absolute bottom-1 left-1/2 -translate-x-1/2 text-slate-400 text-xs font-bold">S</div>
            <div className="absolute left-1 top-1/2 -translate-y-1/2 text-slate-400 text-xs font-bold">W</div>
            <div className="absolute right-1 top-1/2 -translate-y-1/2 text-slate-400 text-xs font-bold">E</div>
            <Compass className="absolute inset-0 m-auto w-6 h-6 text-blue-400" />
          </div>
        </div>
      </div>

      {/* 3D Scene */}
      <div className="absolute inset-0" data-testid="exhibition-3d-scene">
        <ExhibitionScene3D
          booths={positionedBooths}
          facilities={showFacilities ? facilities : []}
          props={sceneProps}
          landmarks={landmarks}
          onBoothSelect={setSelectedBooth}
          selectedBooth={selectedBooth}
          weather={effectsEnabled ? weather : null}
          routeStart={routeEnd ? routeStart : null}
          routeEnd={routeEnd}
          routeActive={!!routeEnd}
          visitorWorldPos={gps.worldPos}
          visitorAccuracy={gps.accuracyMeters}
        />
      </div>

      {/* GPS "Show my location" toggle — bottom-left */}
      <GPSLocateButton gps={gps} />

      {/* Booth Info Panel */}
      {selectedBooth && (
        <BoothInfoPanel
          booth={selectedBooth}
          onClose={() => setSelectedBooth(null)}
          onNavigate={handleNavigateToBooth}
        />
      )}

      {/* Route Navigation Banner (when route is active) */}
      {routeEnd && routeEndBooth && (
        <div className="absolute top-24 left-1/2 -translate-x-1/2 z-30 animate-in slide-in-from-top duration-300" data-testid="route-active-banner">
          <div className="bg-gradient-to-r from-amber-500/90 to-orange-500/90 backdrop-blur-xl rounded-2xl px-4 py-3 border border-amber-400/50 shadow-2xl flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center animate-pulse">
              🎯
            </div>
            <div className="text-white">
              <div className="text-xs font-bold uppercase tracking-wider opacity-90">Navigating to</div>
              <div className="font-black text-sm">{routeEndBooth.booth_number} - {routeEndBooth.company?.name || 'Available Booth'}</div>
            </div>
            <button 
              onClick={clearRoute}
              className="ml-2 p-2 rounded-lg bg-white/20 hover:bg-white/30 text-white transition-colors"
              data-testid="clear-route-btn"
              aria-label="Clear route"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Instructions overlay */}
      {!selectedBooth && showWelcome && (
        <div className="absolute top-24 right-4 z-10 max-w-xs">
          <div className="bg-gradient-to-br from-emerald-900/90 to-emerald-800/80 backdrop-blur-xl rounded-2xl p-4 border border-emerald-500/30 shadow-xl relative">
            <button
              onClick={() => setShowWelcome(false)}
              className="absolute top-2 right-2 p-1 rounded-full hover:bg-emerald-800/50 text-emerald-200 hover:text-white transition-colors"
              data-testid="close-welcome-btn"
              aria-label="Close welcome"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className="flex items-center gap-2 mb-2 pr-4">
              <span className="text-2xl">🌿</span>
              <div>
                <h4 className="text-white font-black text-sm leading-tight">Selamat Datang!</h4>
                <p className="text-emerald-300 text-[10px]">Welcome to Landskap Negara 2026</p>
              </div>
            </div>
            <ul className="text-xs text-emerald-100 space-y-1.5 mt-3">
              <li>🖱️ <span className="text-emerald-300">Drag:</span> Rotate camera</li>
              <li>📜 <span className="text-emerald-300">Scroll:</span> Zoom in/out</li>
              <li>✋ <span className="text-emerald-300">Right-drag:</span> Pan view</li>
              <li>🎯 <span className="text-emerald-300">Click tent:</span> View exhibitor</li>
              <li>☔ <span className="text-emerald-300">Rain:</span> Sheltered booths highlighted</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
